import { useRef, useState } from 'react';

import styles from './AttachmentSection.module.scss';

/**
 * AttachmentSection 컴포넌트
 * - 첨부파일 섹션 컴포넌트 (Figma: Frame 427321494)
 * - 라벨(120px) + 파일 목록 (파란색 링크) 구조
 * - 파일 목록은 가로로 gap 18px 배치
 * - 2줄 기본 노출, 길어질 경우 스크롤 적용
 * - 드롭존 클릭 시 파일 선택 창 표시
 * - 드래그 앤 드롭 지원
 * - 파일 삭제 기능 (x 버튼)
 */

function AttachmentSection({
  attachments = [],
  onAttachmentsChange,
  isPreview = false,
  onFileClick,
  onError,
}) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // 파일 크기 제한 (100MB)
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  // 파일 선택 창 열기
  function handleDropZoneClick() {
    if (fileInputRef.current && !isPreview) {
      fileInputRef.current.click();
    }
  }

  // 파일 크기 검증 및 추가 처리 (공통 함수)
  function addFiles(files) {
    if (!files || files.length === 0 || !onAttachmentsChange) {
      return;
    }

    const fileArray = Array.from(files);
    const validFiles = [];
    const invalidFiles = [];

    // 각 파일의 크기 검증
    fileArray.forEach(function (file) {
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // 크기 초과 파일이 있는 경우 에러 메시지 표시
    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles
        .map(function (file) {
          return file.name;
        })
        .join(', ');
      const errorMessage = `다음 파일의 크기가 100MB를 초과하여 추가할 수 없습니다:\n${fileNames}`;

      if (onError) {
        onError(errorMessage);
      } else {
        // onError가 제공되지 않은 경우 기본 alert 사용
        alert(errorMessage);
      }
    }

    // 유효한 파일만 추가
    if (validFiles.length > 0) {
      const newFiles = validFiles.map(function (file) {
        return {
          id: Date.now() + Math.random(), // 고유 ID 생성
          name: file.name,
          size: file.size,
          file: file, // 실제 File 객체 저장
        };
      });
      onAttachmentsChange([...attachments, ...newFiles]);
    }
  }

  // 파일 선택 시 처리
  function handleFileChange(event) {
    const files = event.target.files;
    addFiles(files);
    // input 초기화 (같은 파일 다시 선택 가능하도록)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  // 드래그 오버 핸들러
  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!isPreview) {
      setIsDragging(true);
    }
  }

  // 드래그 리브 핸들러
  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  // 드롭 핸들러
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isPreview) return;

    const files = e.dataTransfer.files;
    addFiles(files);
  }

  // 파일 삭제 핸들러
  function handleRemoveFile(fileId, fileIndex) {
    if (onAttachmentsChange) {
      // id가 있으면 id로, 없으면 index로 삭제
      if (fileId) {
        onAttachmentsChange(attachments.filter((file) => file.id !== fileId));
      } else {
        onAttachmentsChange(attachments.filter((_, index) => index !== fileIndex));
      }
    }
  }

  // 파일들을 3개씩 그룹화 (피그마에서 한 줄에 여러 파일 표시)
  const groupedFiles = [];
  for (let i = 0; i < attachments.length; i += 3) {
    groupedFiles.push(attachments.slice(i, i + 3));
  }
  // for (let i = 0; i < attachments.length; i += 5) {
  //   groupedFiles.push(attachments.slice(i, i + 5));
  // }

  // 2줄만 표시 (최대 10개 파일, 한 줄에 5개씩)
  const displayFiles = groupedFiles.slice(0, 2);
  const hasMoreFiles = groupedFiles.length > 2;
  const needsScroll = hasMoreFiles;

  if (isPreview) {
    return (
      <div className={styles.attachmentSection}>
        <div className={styles.attachmentRow}>
          <div className={styles.sectionTitle}>첨부파일</div>
          <div className={styles.attachmentContent}>
            {attachments.length > 0 ? (
              <>
                <div className={styles.attachmentInfo}>
                  <div className={styles.fileListContainer}>
                    {/*
                     * 2026.06.29
                     * Preview 첨부파일 표시 로직 변경
                     * - 기존: 최대 10개(2줄)만 표시
                     * - 변경: groupedFiles를 사용하여 전체 첨부파일 표시
                     */}
                    {groupedFiles.map((group, groupIndex) => (
                      <div key={groupIndex} className={styles.fileRowInner}>
                        {group.map((file, index) => (
                          <div key={index} className={styles.attachmentItemWrapper}>
                            <span
                              key={index}
                              className={`${styles.attachmentItem} ${onFileClick ? styles.clickable : ''}`}
                              onClick={() => onFileClick?.(file.idx || file.id)}
                              title={onFileClick ? '클릭하여 다운로드' : undefined}
                            >
                              {file.name || '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                {/* 스크롤바 영역 - 피그마: component : scroll */}
                {/* {needsScroll && (
                  <div className={styles.scrollBar}>
                    <div className={styles.scrollThumb} />
                  </div>
                )} */}
              </>
            ) : (
              <span className={styles.emptyMessage}>첨부파일이 없습니다.</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.attachmentSection}>
      {/* 숨겨진 파일 input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <div className={styles.attachmentRow}>
        <label className={styles.sectionTitle}>첨부파일</label>
        <div
          className={`${styles.attachmentContent} ${isDragging ? styles.dragging : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={attachments.length > 0 ? handleDropZoneClick : undefined}
          role={attachments.length > 0 ? 'button' : undefined}
          tabIndex={attachments.length > 0 ? 0 : undefined}
          onKeyDown={
            attachments.length > 0
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleDropZoneClick();
                  }
                }
              : undefined
          }
        >
          {attachments.length > 0 ? (
            <div className={styles.attachmentInfo}>
              {groupedFiles.map(function (group, groupIndex) {
                return (
                  <div key={groupIndex} className={styles.fileRow}>
                    {group.map(function (file, fileIndex) {
                      // 전체 attachments 배열에서의 실제 인덱스 계산
                      const globalIndex = groupIndex * 5 + fileIndex;
                      const fileId = file.id;
                      return (
                        <div
                          key={fileId || globalIndex}
                          className={styles.attachmentItemWrapper}
                          onClick={(e) => e.stopPropagation()} // 파일 항목 클릭 시 부모의 파일 선택 창이 열리지 않도록
                        >
                          <span className={styles.attachmentItem}>{file.name || '-'}</span>
                          {!isPreview && (
                            <button
                              type="button"
                              className={styles.deleteButton}
                              onClick={() => handleRemoveFile(fileId, globalIndex)}
                              aria-label="파일 삭제"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={`${styles.fileDropZone} ${isDragging ? styles.dragging : ''}`}
              onClick={handleDropZoneClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleDropZoneClick();
                }
              }}
            >
              <p>여기에 파일을 끌어다 놓으세요.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttachmentSection;
